import { bbox } from '@turf/turf';
import dayjs from 'dayjs';
import queryString from 'query-string';

import {
  AOIReadModel,
  AssetReadModel,
  AssetType,
  ExpressionType,
  IndexReadModel,
  Type
} from '@src/clients/atlas/api';
import { SatelliteBand } from '@src/clients/lens/api';
import { MapInstance } from '@src/contexts';
import store from '@src/redux/store';

import { RenderOptions } from './IndiceUtils';
import { getInputRasterPath } from './InsightsUtils';
import { InputRasterUrlHandler } from './MapUtils';

export const verifyFormula = (
  indice: IndexReadModel,
  availableBands: string[]
) => {
  let flag = true;
  const bands = indice.bands || [];
  for (let i = 0; i < bands.length; i += 1) {
    if (!availableBands.includes(bands[i])) {
      flag = false;
      break;
    }
  }

  return flag;
};

export const checkPresetIndiceValidity = (
  indice: IndexReadModel,
  aoiBands: string[] = [],
  allBands: SatelliteBand[] = []
) => {
  const bands: string[] = [];

  allBands.forEach(b => {
    if (indice?.expression?.includes?.(b.name!)) {
      bands.push(b.name!);
    }
  });

  return aoiBands.filter(b => bands.indexOf(b) >= 0).length === bands.length;
};

export const showIndiceName = (indice: IndexReadModel) => {
  return indice.type === Type.Preset ? indice.name.toUpperCase() : indice.name;
};

export function getFormattedTimestampSuffix(): string {
  return dayjs().format('YYYY-MM-DD_HH-mm-ss');
}

export function getDefaultResourceName(prefix: string): string {
  return `${prefix}_${getFormattedTimestampSuffix()}`;
}

export function getSceneUrl({
  scene,
  aoi
}: {
  scene: AssetReadModel;
  aoi: AOIReadModel & { metadata: { preview_url: string } };
}) {
  const state = store.getState();
  const format = state.sourceActions.tileRenderFormat;

  // @ts-expect-error @ts-ignore
  const url = InputRasterUrlHandler({
    url: scene.path,
    activeAoi: aoi,
    assetType: scene.asset_type,
    format
  });

  const bounds = bbox(aoi.geometry) as [number, number, number, number];
  return { url, bounds, id: scene.id };
}

export function setMapTileLayer({
  bounds,
  id,
  mapInstance,
  url,
  opacity,
  beforeId
}: {
  mapInstance: MapInstance;
  bounds: [number, number, number, number];
  id: string;
  url: string;
  opacity?: number;
  beforeId?: string;
}) {
  if (!mapInstance || !url) return;
  mapInstance.addTileLayer({ args: [url, { id, bounds, opacity }], beforeId });
}

export function setInsightRasterLayer({
  insight,
  mapInstance,
  aoi
}: {
  insight: {
    input: Array<{
      format: string;
      name: string;
      type: string;
      stac_url: string;
    }>;
  };
  mapInstance: MapInstance;
  aoi: (AOIReadModel & { metadata: { preview_url: string } }) | null;
}) {
  if (!aoi) return;

  const state = store.getState();
  const format = state.sourceActions.tileRenderFormat;

  const bounds = bbox(aoi.geometry) as [number, number, number, number];

  // @ts-expect-error @ts-ignore
  const url = InputRasterUrlHandler({
    url: getInputRasterPath(insight.input),
    activeAoi: aoi,
    assetType: AssetType.StacItem,
    format
  });

  setMapTileLayer({ bounds, id: url, mapInstance, url });
}

export function setVisualizationLayer({
  mapInstance,
  url,
  type,
  expression_type,
  rescale,
  colormap,
  aoi
}: {
  mapInstance: MapInstance;
  url: string;
  type: IndexReadModel['type'];
  expression_type: IndexReadModel['expression_type'];
  rescale: IndexReadModel['rescale'];
  colormap: IndexReadModel['colormap'];
  aoi: AOIReadModel | null;
}) {
  if (!mapInstance || !aoi) return;

  const isComposite =
    type === Type.Custom && expression_type === ExpressionType.Composite;

  const param: {
    url: string;
    rescale: string;
    colormap_name?: string;
    bidx?: number[];
  } = {
    url,
    rescale: rescale.join('%2C'),
    colormap_name: isComposite ? undefined : colormap,
    bidx: isComposite ? [1, 2, 3] : undefined
  };

  const format = store.getState().sourceActions.tileRenderFormat;

  const titilerUrl = `${
    import.meta.env.VITE_COG_TILER
  }/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?${queryString.stringify(
    param,
    RenderOptions as Parameters<typeof queryString.stringify>[1]
  )}`;

  const bounds = bbox(aoi.geometry) as [number, number, number, number];

  mapInstance.removeAllLayer();
  mapInstance.addTileLayer({ args: [titilerUrl, { id: titilerUrl, bounds }] });
}

export function setInsightOutputLayer({
  url,
  mapInstance,
  insight,
  id,
  aoi,
  opacity,
  beforeId
}: {
  url: string | undefined | null;
  mapInstance: MapInstance;
  insight: {
    input: Array<{
      format: string;
      name: string;
      type: string;
      stac_url: string;
    }>;
  };
  id: string;
  aoi: (AOIReadModel & { metadata: { preview_url: string } }) | null;
  opacity?: number;
  beforeId?: string;
}) {
  if (!mapInstance || !url || !aoi) return;

  const bounds = bbox(aoi.geometry) as [number, number, number, number];

  setInsightRasterLayer({ insight, mapInstance, aoi });
  setMapTileLayer({ mapInstance, bounds, id, url, opacity, beforeId });
}
